import React from 'react';

export interface GraphFilterState {
  minStrength:   number;
  minSourceReliability: number;
  levelFilter:   ('WEAK' | 'MEDIUM' | 'STRONG')[];
  onlyValidated: boolean;
  relationTypes: string[];
  entityTypes:   string[];
  sourceTypes:   string[];
  channelTypes:  string[];
  hopDepth:      1 | 2 | 3;
  timeRange:     '24h' | '7d' | '30d' | null;
}

interface GraphFiltersProps {
  filters:                GraphFilterState;
  availableRelationTypes: string[];
  availableSourceTypes:   string[];
  availableChannelTypes:  string[];
  onChange: (filters: GraphFilterState) => void;
}

const SOURCE_TYPE_LABELS: Record<string, string> = {
  upload_document: 'Documento',
  news_api:        'Notizie',
  social_api:      'Social',
  web_scraping:    'Web',
  rss_feed:        'RSS',
};

// Allineata a CHANNEL_TYPE_LABELS in report-generator.cjs (stesse chiavi,
// stessi valori italiani) — vedi wiki/Pulsar/03-storage-e-grafo-frontend.md
// §4.2. Le due mappe restano file separati (scopi diversi: checkbox UI vs.
// testo prompt) ma le chiavi vanno tenute in sync manualmente.
const CHANNEL_TYPE_LABELS: Record<string, string> = {
  news:               'Articolo',
  broadcast_tv:       'Servizio TV',
  broadcast_radio:    'Servizio radio',
  podcast_transcript: 'Podcast',
  forum_thread:       'Forum/Reddit',
  blog:               'Blog',
  social_post:        'Post social',
  review:             'Recensione',
};

const ENTITY_TYPE_LABELS: Record<string, string> = {
  PERSON:  'Persona',
  ORG:     'Organizzazione',
  PLACE:   'Luogo',
  EVENT:   'Evento',
  CONCEPT: 'Concetto',
  VALUE:   'Valore',
};

const ALL_ENTITY_TYPES = ['PERSON', 'ORG', 'PLACE', 'EVENT', 'CONCEPT', 'VALUE'] as const;

const LEVEL_COLORS: Record<string, string> = {
  STRONG: 'text-green-400',
  MEDIUM: 'text-yellow-400',
  WEAK:   'text-gray-400',
};

export function GraphFilters({ filters, availableRelationTypes, availableSourceTypes, availableChannelTypes, onChange }: GraphFiltersProps) {
  const update = (partial: Partial<GraphFilterState>) => onChange({ ...filters, ...partial });

  const toggleItem = (key: 'relationTypes' | 'sourceTypes' | 'channelTypes' | 'entityTypes', value: string) => {
    const current = filters[key];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    update({ [key]: updated });
  };

  const toggleLevel = (level: 'WEAK' | 'MEDIUM' | 'STRONG') => {
    const current = filters.levelFilter;
    const updated = current.includes(level)
      ? current.filter((v) => v !== level)
      : [...current, level];
    update({ levelFilter: updated });
  };

  return (
    <div className="flex flex-col gap-4 text-sm text-white">

      {/* Strength slider */}
      <div>
        <label className="block text-gray-400 mb-1 text-xs font-semibold uppercase tracking-wide">
          Forza minima: <span className="text-white font-mono">{filters.minStrength.toFixed(2)}</span>
        </label>
        <input
          type="range" min={0} max={1} step={0.05}
          value={filters.minStrength}
          onChange={(e) => update({ minStrength: parseFloat(e.target.value) })}
          className="w-full accent-blue-500"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-0.5">
          <span>WEAK</span><span>MEDIUM</span><span>STRONG</span>
        </div>
      </div>

      {/* Source reliability slider */}
      <div>
        <label className="block text-gray-400 mb-1 text-xs font-semibold uppercase tracking-wide">
          Affidabilità fonte minima: <span className="text-white font-mono">{filters.minSourceReliability.toFixed(2)}</span>
        </label>
        <input
          type="range" min={0} max={1} step={0.05}
          value={filters.minSourceReliability}
          onChange={(e) => update({ minSourceReliability: parseFloat(e.target.value) })}
          className="w-full accent-blue-500"
        />
      </div>

      {/* Level filter */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Livello</p>
        <div className="flex gap-2">
          {(['STRONG', 'MEDIUM', 'WEAK'] as const).map((lvl) => {
            const active = filters.levelFilter.includes(lvl);
            return (
              <button
                key={lvl}
                onClick={() => toggleLevel(lvl)}
                className={`flex-1 py-1 rounded text-xs font-semibold transition-colors border ${
                  active
                    ? lvl === 'STRONG' ? 'bg-green-700/50 border-green-500 text-green-300'
                    : lvl === 'MEDIUM' ? 'bg-yellow-700/50 border-yellow-500 text-yellow-300'
                    : 'bg-gray-700/50 border-gray-500 text-gray-300'
                    : 'bg-gray-800 border-gray-700 text-gray-500 hover:border-gray-500'
                }`}
              >
                {lvl}
              </button>
            );
          })}
        </div>
      </div>

      {/* Only validated toggle */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400">Solo validate</span>
        <button
          onClick={() => update({ onlyValidated: !filters.onlyValidated })}
          className={`relative w-9 h-5 rounded-full transition-colors ${
            filters.onlyValidated ? 'bg-blue-600' : 'bg-gray-600'
          }`}
        >
          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
            filters.onlyValidated ? 'translate-x-4' : 'translate-x-0'
          }`} />
        </button>
      </div>

      {/* Hop depth */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Profondità</p>
        <div className="flex gap-2">
          {([1, 2, 3] as const).map((h) => (
            <button
              key={h}
              onClick={() => update({ hopDepth: h })}
              className={`flex-1 py-1 rounded text-xs font-medium transition-colors ${
                filters.hopDepth === h ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {h} hop
            </button>
          ))}
        </div>
      </div>

      {/* Time range */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Periodo</p>
        <div className="flex gap-1 flex-wrap">
          {([null, '24h', '7d', '30d'] as const).map((tr) => (
            <button
              key={tr ?? 'all'}
              onClick={() => update({ timeRange: tr })}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                filters.timeRange === tr
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {tr ?? 'Tutto'}
            </button>
          ))}
        </div>
      </div>

      {/* Entity types */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Tipo entità</p>
        <div className="flex flex-col gap-1">
          {ALL_ENTITY_TYPES.map((et) => {
            const active = filters.entityTypes.length === 0 || filters.entityTypes.includes(et);
            return (
              <label key={et} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleItem('entityTypes', et)}
                  className="accent-blue-500 w-3 h-3"
                />
                <span className="text-xs">{ENTITY_TYPE_LABELS[et] ?? et}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Relation types */}
      {availableRelationTypes.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Tipo relazione</p>
          <div className="flex flex-col gap-1">
            {availableRelationTypes.map((rt) => (
              <label key={rt} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.relationTypes.length === 0 || filters.relationTypes.includes(rt)}
                  onChange={() => toggleItem('relationTypes', rt)}
                  className="accent-blue-500 w-3 h-3"
                />
                <span className="text-xs">{rt}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Source types */}
      {availableSourceTypes.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Fonte</p>
          <div className="flex flex-col gap-1">
            {availableSourceTypes.map((st) => (
              <label key={st} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.sourceTypes.length === 0 || filters.sourceTypes.includes(st)}
                  onChange={() => toggleItem('sourceTypes', st)}
                  className="accent-blue-500 w-3 h-3"
                />
                <span className="text-xs">{SOURCE_TYPE_LABELS[st] ?? st}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Channel types — sotto-filtro di "Fonte": distingue post/forum/podcast
          dentro il contenitore eterogeneo social_api (es. Pulsar) */}
      {availableChannelTypes.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Canale media</p>
          <div className="flex flex-col gap-1">
            {availableChannelTypes.map((ct) => (
              <label key={ct} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.channelTypes.length === 0 || filters.channelTypes.includes(ct)}
                  onChange={() => toggleItem('channelTypes', ct)}
                  className="accent-blue-500 w-3 h-3"
                />
                <span className="text-xs">{CHANNEL_TYPE_LABELS[ct] ?? ct}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Reset */}
      <button
        onClick={() => onChange({
          minStrength: 0.4,
          minSourceReliability: 0,
          levelFilter: ['MEDIUM', 'STRONG'],
          onlyValidated: true,
          relationTypes: [],
          entityTypes: [],
          sourceTypes: [],
          channelTypes: [],
          hopDepth: 2,
          timeRange: null,
        })}
        className="text-xs text-gray-400 hover:text-white underline text-left"
      >
        Reset filtri
      </button>
    </div>
  );
}
